import { useEffect, useState } from "preact/hooks";
import { useLocation } from "preact-iso/router";

import { isScanning, scanStatus, showToast } from "../store/appStore";

interface Props {
  onStartScan: () => void;
  onOpenSettings: () => void;
  onOpenAddCompany: () => void;
  onOpenDiagnostics: () => void;
}

const scanIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
    <path
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

/** Responsive header. Below 700px: hamburger left, title center, scan right.
 * Above 700px the full action bar sits inline. */
export function Header({ onStartScan, onOpenSettings, onOpenAddCompany, onOpenDiagnostics }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on navigation.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.path]);

  // Close drawer on Escape.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const countdown = scanStatus.value?.next_scan_in;
  const scanning = isScanning.value;
  const scanProgress = scanStatus.value?.progress;

  const scanAriaLabel = scanning
    ? scanProgress
      ? `Scanning jobs, ${scanProgress.current} of ${scanProgress.total}`
      : "Scanning jobs"
    : "Scan jobs now";

  const handleScanClick = () => {
    if (scanning) {
      showToast("Scan in progress — see Diagnostics for live output");
      onOpenDiagnostics();
      return;
    }
    onStartScan();
  };

  return (
    <header class="app-header">
      <button
        class="header-icon-btn hamburger"
        aria-label="Open menu"
        aria-expanded={drawerOpen}
        onClick={() => setDrawerOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M4 6h16M4 12h16M4 18h16" stroke-linecap="round" />
        </svg>
      </button>

      <div class="header-brand">
        <div class="header-titles">
          <p class="eyebrow">Frontier Technology Careers</p>
          <h1>ML Job Tracker</h1>
        </div>
      </div>

      <button
        type="button"
        class={`header-icon-btn header-mobile-scan${scanning ? " is-scanning" : ""}`}
        aria-label={scanAriaLabel}
        aria-busy={scanning}
        onClick={handleScanClick}
      >
        {scanIcon}
      </button>

      <div class="header-actions">
        {countdown && countdown > 0 && (
          <div class="scheduler-countdown" title="Next automatic background scan">
            <span class="pulse-dot" />
            <span>Next auto-scan in {Math.floor(countdown / 60)}m {countdown % 60}s</span>
          </div>
        )}
        <button class="btn-primary btn-add-company" onClick={onOpenAddCompany}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
            <path d="M12 4v16m8-8H4" stroke-linecap="round" />
          </svg>
          <span>Add Company</span>
        </button>
        <button class="btn-primary btn-settings" onClick={onOpenSettings}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
            <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>Settings</span>
        </button>
        <button
          type="button"
          class={`btn-primary btn-scan${scanning ? " is-scanning" : ""}`}
          onClick={handleScanClick}
          aria-busy={scanning}
        >
          {scanIcon}
          <span>{scanning ? "Scanning…" : "Scan Jobs Now"}</span>
          <span class="btn-arrow" aria-hidden="true">→</span>
        </button>
      </div>

      {drawerOpen && (
        <div class="header-drawer" role="dialog" aria-label="Menu">
          <button class="drawer-item" onClick={() => { onOpenAddCompany(); setDrawerOpen(false); }}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5"><path d="M12 4v16m8-8H4" stroke-linecap="round" /></svg>
            <span>Add Company</span>
          </button>
          <button class="drawer-item" onClick={() => { onOpenSettings(); setDrawerOpen(false); }}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Settings</span>
          </button>
          <button class="drawer-item" onClick={() => { onOpenDiagnostics(); setDrawerOpen(false); }}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span>Diagnostics</span>
          </button>
        </div>
      )}
    </header>
  );
}
